import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Search as SearchIcon,
  AccessTimeOutlined,
  EditOutlined,
  HourglassEmptyOutlined,
  CheckOutlined,
  CalendarTodayOutlined,
  ScienceOutlined,
  VisibilityOutlined,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CheckCircleOutline as AcceptIcon,
  Block as RejectIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { specimenService, lisDashboardService } from "../../services/lis";

// 1. Stat Card component matching the screenshot
function StatCard({ title, subtitle, value, icon, color, bgColor, isDark }) {
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textTitleColor = isDark ? "#E2E8F0" : "#334155";
  const textSubtitleColor = isDark ? "#94A3B8" : "#94A3B8";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: "12px",
        bgcolor: cardBg,
        border: `1px solid ${borderColor}`,
        boxShadow: isDark
          ? "0 2px 10px rgba(0,0,0,0.3)"
          : "0 2px 10px rgba(15, 23, 42, 0.03)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "8px",
          bgcolor: isDark ? `${color}25` : bgColor,
          color: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.76rem",
            fontWeight: 600,
            color: textTitleColor,
            lineHeight: 1.15,
            mb: 0.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "1.35rem",
            fontWeight: 800,
            color: color,
            lineHeight: 1.1,
            mb: 0.2,
          }}
        >
          {value ?? "—"}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.68rem",
            color: textSubtitleColor,
            fontWeight: 400,
            lineHeight: 1.1,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

// 2. Status Badge Pill matching screenshot
function StatusBadge({ status, isDark }) {
  const statusStyles = {
    Ordered: {
      color: "#D97706",
      bg: isDark ? "rgba(217, 119, 6, 0.15)" : "#FFFBEB",
      border: isDark ? "rgba(217, 119, 6, 0.3)" : "#FEF3C7",
    },
    "Sample Collected": {
      color: "#2563EB",
      bg: isDark ? "rgba(37, 99, 235, 0.15)" : "#EFF6FF",
      border: isDark ? "rgba(37, 99, 235, 0.3)" : "#DBEAFE",
    },
    "Result Pending": {
      color: isDark ? "#FBBF24" : "#D97706",
      bg: isDark ? "rgba(217, 119, 6, 0.15)" : "#FEF3C7",
      border: isDark ? "rgba(217, 119, 6, 0.3)" : "#FDE68A",
    },
    Completed: {
      color: "#16A34A",
      bg: isDark ? "rgba(22, 163, 74, 0.15)" : "#F0FDF4",
      border: isDark ? "rgba(22, 163, 74, 0.3)" : "#DCFCE7",
    },
    Accepted: {
      color: "#059669",
      bg: isDark ? "rgba(5, 150, 105, 0.15)" : "#ECFDF5",
      border: isDark ? "rgba(5, 150, 105, 0.3)" : "#A7F3D0",
    },
    Rejected: {
      color: "#DC2626",
      bg: isDark ? "rgba(220, 38, 38, 0.15)" : "#FEF2F2",
      border: isDark ? "rgba(220, 38, 38, 0.3)" : "#FECACA",
    },
  };

  const style = statusStyles[status] || {
    color: "#64748B",
    bg: isDark ? "rgba(100, 116, 139, 0.15)" : "#F1F5F9",
    border: isDark ? "rgba(100, 116, 139, 0.3)" : "#E2E8F0",
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 0.8,
        py: 0.1,
        height: 18,
        borderRadius: "4px",
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontFamily: "'Satoshi', sans-serif",
        fontSize: "0.64rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </Box>
  );
}

// Date range helper for dynamic current month
const getDynamicDateRanges = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const fmt = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  return [
    { label: "Today", value: "Today" },
    { label: "Last 7 Days", value: "Last 7 Days" },
    { label: `This Month (${fmt(start)} - ${fmt(end)})`, value: "This Month" },
    { label: `This Year (${year})`, value: "This Year" },
    { label: `Year ${year - 1}`, value: `Year ${year - 1}` },
    { label: `Year ${year - 2}`, value: `Year ${year - 2}` },
    { label: "All Time", value: "All Time" },
  ];
};

export default function SamplesQueue({
  isDark,
  onCollectSample,
  onEnterResult,
  onViewReport,
}) {
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";

  const [stats, setStats] = useState({
    pendingCollection: 0,
    samplesCollected: 0,
    resultPending: 0,
    completed: 0,
  });

  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [statusTabIndex, setStatusTabIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Date range popover state
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [dateRangeText, setDateRangeText] = useState("All Time");
  const [customYearInput, setCustomYearInput] = useState("");

  // Specimen Acceptance & Rejection Dialog State
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedSampleForReject, setSelectedSampleForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("Hemolyzed");
  const [rejectNotes, setRejectNotes] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [selectedSampleForAccept, setSelectedSampleForAccept] = useState(null);
  const [acceptNotes, setAcceptNotes] = useState("");
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);

  const REJECTION_REASONS_LIST = [
    "Hemolyzed",
    "Lipemic",
    "Icteric",
    "Clotted",
    "Insufficient quantity",
    "Wrong container",
    "Mislabeled",
    "Unlabeled",
    "Improper storage",
    "Improper transport",
    "Delayed transport",
    "Contaminated",
    "Broken/leaking container",
    "Incorrect specimen type",
    "Other",
  ];

  const handleOpenRejectDialog = (sample, e) => {
    e?.stopPropagation();
    setSelectedSampleForReject(sample);
    setRejectReason("Hemolyzed");
    setRejectNotes("");
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    const specimenId = selectedSampleForReject?.raw?._id || selectedSampleForReject?.id;
    if (!specimenId) return;
    setRejectSubmitting(true);
    try {
      await specimenService.reject(specimenId, rejectReason, rejectNotes);
      setRejectDialogOpen(false);
      setSelectedSampleForReject(null);
      fetchData();
    } catch (err) {
      console.error("Failed to reject specimen:", err);
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleOpenAcceptDialog = (sample, e) => {
    e?.stopPropagation();
    setSelectedSampleForAccept(sample);
    setAcceptNotes("");
    setAcceptDialogOpen(true);
  };

  const handleConfirmAccept = async () => {
    const specimenId = selectedSampleForAccept?.raw?._id || selectedSampleForAccept?.id;
    if (!specimenId) return;
    setAcceptSubmitting(true);
    try {
      await specimenService.accept(specimenId, acceptNotes);
      setAcceptDialogOpen(false);
      setSelectedSampleForAccept(null);
      fetchData();
    } catch (err) {
      console.error("Failed to accept specimen:", err);
    } finally {
      setAcceptSubmitting(false);
    }
  };

  // Fetch real data from backend
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        lisDashboardService.getSamplesStats().catch(() => null),
        specimenService.list({ page: 1, limit: 200 }).catch(() => null),
      ]);

      if (statsRes?.data?.data) {
        setStats({
          pendingCollection: statsRes.data.data.pendingCollection ?? 0,
          samplesCollected:
            statsRes.data.data.collected ?? statsRes.data.data.samplesCollected ?? 0,
          resultPending:
            statsRes.data.data.inProcess ?? statsRes.data.data.resultPending ?? 0,
          completed:
            statsRes.data.data.completed ?? statsRes.data.data.resultsReleased ?? 0,
        });
      }

      if (listRes?.data?.data && listRes.data.data.length > 0) {
        const mapped = listRes.data.data.map((item, idx) => ({
          id: item._id || `item-${idx}`,
          date: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
          orderNumber:
            item.labOrder?.orderNumber || item.labOrder?.orderId || item.orderId || `ORD-${new Date().getFullYear()}-${100 + idx}`,
          sampleId: item.sampleBarcode || item.sampleId || item.specimenId || "—",
          patient: {
            name:
              item.patient?.firstName
                ? `${item.patient.firstName} ${item.patient.lastName || ""}`
                : item.labOrder?.patientName || "Patient",
            patientId: item.patient?.patientId || item.patient?.mrn || `PT-250512-00${idx + 1}`,
          },
          testName:
            item.testsUsingThisSample?.[0]?.test?.displayName ||
            item.testName ||
            "Full Blood Count (FBC)",
          sampleType: item.specimenType || "Blood",
          containerType: item.containerType || "",
          currentLocation: item.currentLocation || (item.status === "PENDING" ? "Awaiting Collection" : "Collection Room"),
          bodySite: item.bodySite || "",
          laterality: item.laterality || "",
          bodySiteDetail: item.bodySiteDetail || "",
          status: (() => {
            const orderStatus = (
              item.labOrder?.orderStatus ||
              item.labOrder?.status ||
              ""
            ).toUpperCase();
            if (
              [
                "COMPLETED",
                "VALIDATED",
                "AUTHORIZED",
                "RELEASED",
                "RESULTED",
              ].includes(orderStatus)
            ) {
              return "Completed";
            }
            const s = (item.status || "").toUpperCase();
            if (["ACCEPTED"].includes(s)) return "Accepted";
            if (["REJECTED"].includes(s)) return "Rejected";
            if (["COMPLETED", "RESULTED", "RELEASED"].includes(s)) return "Completed";
            if (
              ["PROCESSING", "IN_PROGRESS", "IN_PROCESS", "RESULT_ENTERED"].includes(s)
            ) {
              return "Result Pending";
            }
            if (["COLLECTED", "RECEIVED", "IN_TRANSIT"].includes(s)) {
              return "Sample Collected";
            }
            if (s === "PENDING" || s === "ORDERED" || item.isUncollectedOrder) {
              return "Ordered";
            }
            const sid = item.sampleBarcode || item.sampleId || item.specimenId;
            return sid && sid !== "—" ? "Sample Collected" : "Ordered";
          })(),
          raw: item,
        }));
        setSamples(mapped);
      } else {
        setSamples([]);
      }
    } catch (err) {
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Dynamic tab counts derived directly from samples array
  const statusCounts = useMemo(() => {
    let pending = 0;
    let collected = 0;
    let resultPending = 0;
    let completed = 0;

    samples.forEach((item) => {
      const isPending =
        item.status === "Ordered" ||
        item.sampleId === "—" ||
        item.raw?.isUncollectedOrder;
      const isCollected =
        item.sampleId && item.sampleId !== "—" && !isPending;
      if (isPending) pending++;
      if (isCollected) collected++;
      if (
        item.status === "Result Pending" ||
        (isCollected && item.status !== "Completed")
      ) {
        resultPending++;
      }
      if (item.status === "Completed") completed++;
    });

    return {
      pending: samples.length > 0 ? pending : stats.pendingCollection || 0,
      collected: samples.length > 0 ? collected : stats.samplesCollected || 0,
      resultPending: samples.length > 0 ? resultPending : stats.resultPending || 0,
      completed: samples.length > 0 ? completed : stats.completed || 0,
    };
  }, [samples, stats]);

  // Filtered samples
  const filteredSamples = useMemo(() => {
    return samples.filter((item) => {
      const q = searchQuery.toLowerCase().trim();
      const itemDateStr =
        item.raw?.createdAt ||
        item.raw?.collectionTimestamp ||
        item.raw?.orderedAt ||
        item.raw?.labOrder?.orderedAt;
      const isYearQuery = /^\d{4}$/.test(q);
      const matchesYear = isYearQuery && itemDateStr && new Date(itemDateStr).getFullYear() === parseInt(q, 10);

      const matchesSearch =
        !q ||
        matchesYear ||
        item.orderNumber?.toLowerCase().includes(q) ||
        item.patient?.name?.toLowerCase().includes(q) ||
        item.patient?.patientId?.toLowerCase().includes(q) ||
        item.sampleId?.toLowerCase().includes(q) ||
        item.testName?.toLowerCase().includes(q) ||
        item.sampleType?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q);

      // Status filtering matching workstation tabs
      let matchesStatus = true;
      if (statusFilter !== "All Status") {
        if (statusFilter === "Ordered" || statusFilter === "Pending Collection") {
          matchesStatus =
            item.status === "Ordered" ||
            item.sampleId === "—" ||
            item.raw?.isUncollectedOrder;
        } else if (
          statusFilter === "Collected" ||
          statusFilter === "Sample Collected"
        ) {
          // Any sample that has been collected (has a sample ID and is not pending collection)
          matchesStatus =
            item.sampleId && item.sampleId !== "—" && item.status !== "Ordered";
        } else if (statusFilter === "Result Pending") {
          matchesStatus =
            item.status === "Result Pending" ||
            (item.sampleId &&
              item.sampleId !== "—" &&
              item.status !== "Completed" &&
              item.status !== "Ordered");
        } else if (statusFilter === "Completed") {
          matchesStatus = item.status === "Completed";
        } else {
          matchesStatus =
            item.status.toLowerCase() === statusFilter.toLowerCase();
        }
      }

      // Date Range filter
      let matchesDate = true;
      if (dateRangeText && dateRangeText !== "All Time") {
        if (itemDateStr) {
          const d = new Date(itemDateStr);
          const now = new Date();
          if (dateRangeText === "Today") {
            matchesDate = d.toDateString() === now.toDateString();
          } else if (dateRangeText === "Last 7 Days") {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(now.getDate() - 7);
            sevenDaysAgo.setHours(0, 0, 0, 0);
            matchesDate = d >= sevenDaysAgo;
          } else if (dateRangeText.startsWith("This Month")) {
            matchesDate =
              d.getMonth() === now.getMonth() &&
              d.getFullYear() === now.getFullYear();
          } else if (dateRangeText.startsWith("This Year")) {
            matchesDate = d.getFullYear() === now.getFullYear();
          } else if (dateRangeText.startsWith("Year ")) {
            const yr = parseInt(dateRangeText.replace("Year ", ""), 10);
            matchesDate = d.getFullYear() === yr;
          }
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [samples, searchQuery, statusFilter, dateRangeText]);

  const totalPages = Math.ceil(filteredSamples.length / pageSize) || 1;
  const paginatedSamples = filteredSamples.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Handle action click based on sample status
  const handleActionClick = (sample) => {
    if (sample.status === "Ordered") {
      onCollectSample?.(
        sample.raw?.orderId_raw ||
        sample.raw?.labOrder?._id ||
        sample.raw?.orderId ||
        sample.id
      );
    } else if (
      sample.status === "Sample Collected" ||
      sample.status === "Result Pending"
    ) {
      onEnterResult?.(
        sample.raw?.testsUsingThisSample?.[0]?._id || sample.raw?.orderItemId,
        sample.raw?._id || sample.id,
        sample.raw?.labOrder?._id || sample.raw?.orderId_raw || sample.raw?.orderId
      );
    } else if (sample.status === "Completed") {
      onViewReport?.(
        sample.raw?.labOrder?._id || sample.raw?.resultId || sample.id
      );
    }
  };

  const STATUS_TABS = [
    { label: "All", filter: "All Status", count: samples.length },
    {
      label: "Pending Collection",
      filter: "Ordered",
      count: statusCounts.pending,
      color: "#EA580C",
    },
    {
      label: "Collected",
      filter: "Collected",
      count: statusCounts.collected,
      color: "#2563EB",
    },
    {
      label: "Result Pending",
      filter: "Result Pending",
      count: statusCounts.resultPending,
      color: "#D97706",
    },
    {
      label: "Completed",
      filter: "Completed",
      count: statusCounts.completed,
      color: "#16A34A",
    },
  ];

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {/* Header Bar matching RIS Worklist */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "1rem",
              fontWeight: 800,
              color: textPrimary,
              lineHeight: 1.2,
            }}
          >
            Samples Queue
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.7rem",
              color: textSecondary,
            }}
          >
            Track collected samples, pending collections, and result entry — {filteredSamples.length} samples
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={fetchData}
            disabled={loading}
            sx={{ width: 26, height: 26, color: textSecondary }}
          >
            {loading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status Tabs with Badges matching RIS/PACS */}
      <Tabs
        value={statusTabIndex}
        onChange={(_, v) => {
          setStatusTabIndex(v);
          setStatusFilter(STATUS_TABS[v].filter);
          setPage(1);
        }}
        sx={{
          mb: 0.5,
          borderBottom: 1,
          borderColor,
          minHeight: 28,
          "& .MuiTabs-indicator": {
            height: "2px",
            bgcolor: STATUS_TABS[statusTabIndex]?.color || "#2563EB",
          },
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {STATUS_TABS.map((t, idx) => (
          <Tab
            key={t.label}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <span>{t.label}</span>
                {t.count > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      minWidth: 16,
                      height: 16,
                      px: 0.75,
                      borderRadius: 9999,
                      ml: 0.25,
                      flexShrink: 0,
                      bgcolor: t.color || (isDark ? "rgba(255,255,255,0.18)" : "#E2E8F0"),
                      color: t.color ? "#FFFFFF" : textPrimary,
                    }}
                  >
                    {t.count}
                  </Box>
                )}
              </Box>
            }
            sx={{
              textTransform: "none",
              minWidth: "auto",
              px: 1.2,
              py: 0.25,
              minHeight: 28,
              fontSize: "0.75rem",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              color: textSecondary,
              "&.Mui-selected": {
                color: t.color || (isDark ? "#FFFFFF" : "#0F172A"),
              },
            }}
          />
        ))}
      </Tabs>

      {/* Filter Bar (Search & Date Range) */}
      <Paper
        elevation={0}
        sx={{
          p: "4px 8px",
          mb: 0.75,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          borderRadius: "6px",
          border: `1px solid ${borderColor}`,
          bgcolor: cardBg,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by patient, order ID, sample ID, or test..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: textSecondary, fontSize: 14 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 220,
            flexGrow: 1,
            "& .MuiOutlinedInput-root": {
              height: 26,
              fontSize: "0.76rem",
              fontFamily: "'Satoshi', sans-serif",
              bgcolor: isDark ? "#141414" : "#FFFFFF",
              borderRadius: "4px",
              "& fieldset": { borderColor },
              "&:hover fieldset": { borderColor: "#3B82F6" },
            },
            "& .MuiOutlinedInput-input": { py: 0.25, px: 0.5 },
          }}
        />

        <Button
          size="small"
          onClick={(e) => setDateAnchorEl(e.currentTarget)}
          startIcon={<CalendarTodayOutlined sx={{ fontSize: 13, color: textSecondary }} />}
          sx={{
            borderRadius: "4px",
            border: `1px solid ${borderColor}`,
            textTransform: "none",
            color: textPrimary,
            px: 1,
            height: 26,
            fontSize: "0.74rem",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 500,
            bgcolor: isDark ? "#141414" : "#FFFFFF",
            "&:hover": {
              borderColor: "#3B82F6",
            },
          }}
        >
          {dateRangeText}
        </Button>

        <Popover
          open={Boolean(dateAnchorEl)}
          anchorEl={dateAnchorEl}
          onClose={() => setDateAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              p: 1,
              bgcolor: cardBg,
              borderRadius: "6px",
              border: `1px solid ${borderColor}`,
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: textPrimary, mb: 0.5 }}>
            Select Range
          </Typography>
          {getDynamicDateRanges().map((opt) => (
            <MenuItem
              key={opt.value}
              onClick={() => {
                setDateRangeText(opt.value);
                setCustomYearInput("");
                setDateAnchorEl(null);
              }}
              sx={{
                fontSize: "0.74rem",
                borderRadius: "4px",
                py: 0.4,
                fontWeight: dateRangeText === opt.value ? 700 : 400,
                bgcolor:
                  dateRangeText === opt.value
                    ? isDark
                      ? "rgba(37,99,235,0.15)"
                      : "#EFF6FF"
                    : "transparent",
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
          {/* Custom year input */}
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${borderColor}` }}>
            <Typography sx={{ fontSize: "0.7rem", color: textSecondary, mb: 0.5 }}>Specific Year</Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <input
                type="number"
                placeholder="e.g. 2023"
                value={customYearInput}
                onChange={(e) => setCustomYearInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const yr = parseInt(customYearInput, 10);
                    if (yr >= 2000 && yr <= new Date().getFullYear() + 1) {
                      setDateRangeText(`Year ${yr}`);
                      setDateAnchorEl(null);
                    }
                  }
                }}
                style={{
                  width: "90px",
                  padding: "3px 6px",
                  fontSize: "0.74rem",
                  borderRadius: "4px",
                  border: `1px solid ${borderColor}`,
                  background: isDark ? "#121212" : "#FFFFFF",
                  color: textPrimary,
                  fontFamily: "'Satoshi', sans-serif",
                  outline: "none",
                }}
              />
              <button
                onClick={() => {
                  const yr = parseInt(customYearInput, 10);
                  if (yr >= 2000 && yr <= new Date().getFullYear() + 1) {
                    setDateRangeText(`Year ${yr}`);
                    setDateAnchorEl(null);
                  }
                }}
                style={{
                  padding: "3px 8px",
                  fontSize: "0.72rem",
                  borderRadius: "4px",
                  border: "none",
                  background: "#3B82F6",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                Go
              </button>
            </Box>
          </Box>
        </Popover>

        {(searchQuery || dateRangeText !== "All Time" || statusTabIndex !== 0) && (
          <Button
            size="small"
            onClick={() => {
              setSearchQuery("");
              setDateRangeText("All Time");
              setStatusTabIndex(0);
              setStatusFilter("All Status");
              setPage(1);
            }}
            sx={{
              height: 26,
              fontSize: "0.72rem",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              color: textSecondary,
              py: 0,
              px: 1,
            }}
          >
            Clear
          </Button>
        )}

        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: textSecondary, ml: "auto", fontFamily: "'Satoshi', sans-serif" }}>
          {filteredSamples.length} shown
        </Typography>
      </Paper>

      {/* Samples Queue Table Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "6px",
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          overflow: "hidden",
        }}
      >

        {/* Table Content */}
        <TableContainer>
          <Table size="small" sx={{ minWidth: 950 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC" }}>
                {[
                  "Date",
                  "Order ID",
                  "Sample ID",
                  "Patient",
                  "Test / Profile",
                  "Specimen Type",
                  "Status",
                  "Action",
                ].map((header) => (
                  <TableCell
                    key={header}
                    align={header === "Action" ? "right" : "left"}
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      color: textSecondary,
                      py: "3px !important",
                      px: "6px !important",
                      borderBottom: `1px solid ${borderColor}`,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} sx={{ color: "#2563EB" }} />
                  </TableCell>
                </TableRow>
              ) : paginatedSamples.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{ py: 4, color: textSecondary, fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem" }}
                  >
                    No samples found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSamples.map((sample) => (
                  <TableRow
                    key={sample.id || sample.orderNumber}
                    hover
                    sx={{
                      height: "28px",
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": {
                        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
                      },
                    }}
                  >
                    {/* 1. Date */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.72rem",
                        color: textSecondary,
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sample.date}
                    </TableCell>

                    {/* 2. Order ID */}
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        fontSize: "0.74rem",
                        color: "#2563EB",
                        cursor: "pointer",
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => handleActionClick(sample)}
                    >
                      {sample.orderNumber}
                    </TableCell>

                    {/* 3. Sample ID & Location */}
                    <TableCell
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.72rem", color: textPrimary }}>
                          {sample.sampleId || "—"}
                        </Typography>
                        {sample.sampleId && sample.sampleId !== "—" && (
                          <Chip
                            size="small"
                            label={sample.currentLocation || "Collection Room"}
                            sx={{
                              height: 16,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              color: "#059669",
                              bgcolor: isDark ? "rgba(5, 150, 105, 0.15)" : "#ECFDF5",
                              border: "1px solid rgba(5, 150, 105, 0.2)",
                              "& .MuiChip-label": { px: 0.4 },
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* 4. Patient (Single-Line) */}
                    <TableCell
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.74rem",
                            color: textPrimary,
                            maxWidth: 150,
                          }}
                        >
                          {sample.patient?.name || "—"}
                        </Typography>
                        {sample.patient?.patientId && (
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "'Satoshi', sans-serif",
                              fontSize: "0.64rem",
                              color: textSecondary,
                            }}
                          >
                            · {sample.patient.patientId}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* 5. Test / Profile (Single-Line) */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.72rem",
                        color: textPrimary,
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography noWrap sx={{ fontSize: "0.72rem", color: textPrimary, maxWidth: 180 }}>
                        {sample.testName || "—"}
                      </Typography>
                    </TableCell>

                    {/* 6. Sample Type & Anatomical Site (Single-Line) */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.72rem",
                        color: textPrimary,
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography noWrap sx={{ fontSize: "0.72rem", color: textPrimary, maxWidth: 170 }}>
                        {sample.sampleType || "Blood"}
                        {sample.bodySite || sample.laterality
                          ? ` · ${[sample.laterality, sample.bodySite].filter(Boolean).join(" ")}`
                          : sample.containerType
                          ? ` (${sample.containerType})`
                          : ""}
                      </Typography>
                    </TableCell>

                    {/* 7. Status */}
                    <TableCell
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <StatusBadge status={sample.status} isDark={isDark} />
                    </TableCell>

                    {/* 8. Action Buttons (Compact height: 24px) */}
                    <TableCell
                      align="right"
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sample.status === "Ordered" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(sample)}
                          startIcon={<ScienceOutlined sx={{ fontSize: 13 }} />}
                          sx={{
                            borderRadius: "4px",
                            borderColor: "#F97316",
                            color: "#EA580C",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            px: 0.8,
                            py: 0.1,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#EA580C",
                              bgcolor: isDark ? "rgba(234, 88, 12, 0.1)" : "#FFF7ED",
                            },
                          }}
                        >
                          Collect Sample
                        </Button>
                      )}

                      {sample.status === "Sample Collected" && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => handleOpenAcceptDialog(sample, e)}
                            startIcon={<AcceptIcon sx={{ fontSize: 13 }} />}
                            sx={{
                              borderRadius: "4px",
                              borderColor: "#059669",
                              color: "#059669",
                              textTransform: "none",
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              px: 0.7,
                              py: 0.1,
                              height: 24,
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderColor: "#047857",
                                bgcolor: isDark ? "rgba(5, 150, 105, 0.1)" : "#ECFDF5",
                              },
                            }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleActionClick(sample)}
                            startIcon={<ScienceOutlined sx={{ fontSize: 13 }} />}
                            sx={{
                              borderRadius: "4px",
                              borderColor: "#3B82F6",
                              color: "#2563EB",
                              textTransform: "none",
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              px: 0.7,
                              py: 0.1,
                              height: 24,
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderColor: "#1D4ED8",
                                bgcolor: isDark ? "rgba(37, 99, 235, 0.1)" : "#EFF6FF",
                              },
                            }}
                          >
                            Enter Result
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => handleOpenRejectDialog(sample, e)}
                            startIcon={<RejectIcon sx={{ fontSize: 13 }} />}
                            sx={{
                              borderRadius: "4px",
                              borderColor: "#DC2626",
                              color: "#DC2626",
                              textTransform: "none",
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              px: 0.7,
                              py: 0.1,
                              height: 24,
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderColor: "#B91C1C",
                                bgcolor: isDark ? "rgba(220, 38, 38, 0.1)" : "#FEF2F2",
                              },
                            }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      )}

                      {sample.status === "Accepted" && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleActionClick(sample)}
                            startIcon={<ScienceOutlined sx={{ fontSize: 13 }} />}
                            sx={{
                              borderRadius: "4px",
                              borderColor: "#2563EB",
                              color: "#2563EB",
                              textTransform: "none",
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              px: 0.8,
                              py: 0.1,
                              height: 24,
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderColor: "#1D4ED8",
                                bgcolor: isDark ? "rgba(37, 99, 235, 0.1)" : "#EFF6FF",
                              },
                            }}
                          >
                            Enter Result
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => handleOpenRejectDialog(sample, e)}
                            startIcon={<RejectIcon sx={{ fontSize: 13 }} />}
                            sx={{
                              borderRadius: "4px",
                              borderColor: "#DC2626",
                              color: "#DC2626",
                              textTransform: "none",
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              px: 0.7,
                              py: 0.1,
                              height: 24,
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderColor: "#B91C1C",
                                bgcolor: isDark ? "rgba(220, 38, 38, 0.1)" : "#FEF2F2",
                              },
                            }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      )}

                      {sample.status === "Rejected" && (
                        <Tooltip title={sample.raw?.rejectionNotes ? `Notes: ${sample.raw.rejectionNotes}` : "Specimen Rejected"} arrow>
                          <Chip
                            size="small"
                            label={sample.raw?.rejectionReason ? `Rejected: ${sample.raw.rejectionReason}` : "Rejected"}
                            sx={{
                              bgcolor: isDark ? "rgba(220, 38, 38, 0.15)" : "#FEF2F2",
                              color: "#DC2626",
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              height: 20,
                              border: "1px solid rgba(220, 38, 38, 0.3)",
                              maxWidth: 180,
                            }}
                          />
                        </Tooltip>
                      )}

                      {sample.status === "Result Pending" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(sample)}
                          startIcon={<EditOutlined sx={{ fontSize: 13 }} />}
                          sx={{
                            borderRadius: "4px",
                            borderColor: isDark ? "#D97706" : "#F59E0B",
                            color: isDark ? "#FBBF24" : "#D97706",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            px: 0.8,
                            py: 0.1,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#B45309",
                              bgcolor: isDark ? "rgba(217, 119, 6, 0.1)" : "#FEF3C7",
                            },
                          }}
                        >
                          Continue Result
                        </Button>
                      )}

                      {sample.status === "Completed" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(sample)}
                          startIcon={<VisibilityOutlined sx={{ fontSize: 13 }} />}
                          sx={{
                            borderRadius: "4px",
                            borderColor: "#22C55E",
                            color: "#16A34A",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            px: 0.8,
                            py: 0.1,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#15803D",
                              bgcolor: isDark ? "rgba(22, 163, 74, 0.1)" : "#F0FDF4",
                            },
                          }}
                        >
                          View Result
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 5. Pagination Footer */}
        <Box
          sx={{
            px: 1.5,
            py: 0.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `1px solid ${borderColor}`,
            bgcolor: isDark ? "#171717" : "#FFFFFF",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.72rem",
              color: textSecondary,
            }}
          >
            Showing {(page - 1) * pageSize + 1} -{" "}
            {Math.min(page * pageSize, filteredSamples.length)} of {filteredSamples.length} samples
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.7rem", color: textSecondary, fontFamily: "'Satoshi', sans-serif" }}>
              Rows:
            </Typography>
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontFamily: "'Satoshi', sans-serif",
                "& .MuiSelect-select": { py: 0, px: 0.75 },
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: "0.74rem" }}>
                  {n}
                </MenuItem>
              ))}
            </Select>

            <IconButton
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{ width: 24, height: 24 }}
            >
              <KeyboardArrowLeft sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                fontFamily: "'Satoshi', sans-serif",
                color: textPrimary,
              }}
            >
              {page} / {totalPages}
            </Typography>
            <IconButton
              size="small"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              sx={{ width: 24, height: 24 }}
            >
              <KeyboardArrowRight sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Accept Specimen Dialog */}
      <Dialog
        open={acceptDialogOpen}
        onClose={() => !acceptSubmitting && setAcceptDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "460px" },
            maxWidth: "480px !important",
            maxHeight: "85vh",
            borderRadius: "14px",
            bgcolor: cardBg,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${borderColor}` }}>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: textPrimary }}>
            Accept Specimen
          </Typography>
          <IconButton
            size="small"
            onClick={() => setAcceptDialogOpen(false)}
            disabled={acceptSubmitting}
            sx={{ color: textSecondary }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 1.75, overflowY: "auto" }}>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem", color: textSecondary, mb: 1.5 }}>
            Confirm acceptance of specimen <b>{selectedSampleForAccept?.sampleId}</b> ({selectedSampleForAccept?.sampleType}) for patient <b>{selectedSampleForAccept?.patient?.name}</b>.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Acceptance Notes (Optional)"
            placeholder="e.g., Sample intact, appropriate volume, placed in processing queue"
            value={acceptNotes}
            onChange={(e) => setAcceptNotes(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.85rem",
                bgcolor: isDark ? "#141414" : "#FFFFFF",
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}` }}>
          <Button
            onClick={() => setAcceptDialogOpen(false)}
            disabled={acceptSubmitting}
            sx={{ textTransform: "none", fontFamily: "'Satoshi', sans-serif", color: textSecondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAccept}
            disabled={acceptSubmitting}
            sx={{
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 700,
              bgcolor: "#059669",
              "&:hover": { bgcolor: "#047857" },
              borderRadius: "8px",
              px: 2.5,
            }}
          >
            {acceptSubmitting ? <CircularProgress size={18} sx={{ color: "#FFF" }} /> : "Confirm Accept"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Specimen Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !rejectSubmitting && setRejectDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "480px" },
            maxWidth: "500px !important",
            maxHeight: "85vh",
            borderRadius: "14px",
            bgcolor: cardBg,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${borderColor}` }}>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "#DC2626" }}>
            Reject Specimen
          </Typography>
          <IconButton
            size="small"
            onClick={() => setRejectDialogOpen(false)}
            disabled={rejectSubmitting}
            sx={{ color: textSecondary }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 1.75, overflowY: "auto" }}>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem", color: textSecondary, mb: 1.5 }}>
            Select the primary rejection reason for specimen <b>{selectedSampleForReject?.sampleId}</b>:
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem" }}>
              Rejection Reason *
            </InputLabel>
            <Select
              value={rejectReason}
              label="Rejection Reason *"
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{
                borderRadius: "8px",
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.85rem",
                bgcolor: isDark ? "#141414" : "#FFFFFF",
              }}
            >
              {REJECTION_REASONS_LIST.map((r) => (
                <MenuItem key={r} value={r} sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem" }}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Rejection Notes / Corrective Actions"
            placeholder="e.g., Requested recollect from ward, specimen was hemolyzed"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.85rem",
                bgcolor: isDark ? "#141414" : "#FFFFFF",
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}` }}>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={rejectSubmitting}
            sx={{ textTransform: "none", fontFamily: "'Satoshi', sans-serif", color: textSecondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReject}
            disabled={rejectSubmitting || !rejectReason}
            sx={{
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 700,
              bgcolor: "#DC2626",
              "&:hover": { bgcolor: "#B91C1C" },
              borderRadius: "8px",
              px: 2.5,
            }}
          >
            {rejectSubmitting ? <CircularProgress size={18} sx={{ color: "#FFF" }} /> : "Reject Specimen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
